import { makeAutoObservable, runInAction } from 'mobx';
import { inviteCodeApi } from '@/services/inviteCodeApi';
import type {
  InviteCode as ApiInviteCode,
  BatchSendInviteCodesReply,
  CreateInviteCodeReply,
  CreateInviteCodeRequest,
} from '@/types';
import { InviteCodeStatus } from '@/types';
import { brandStore } from './brandStore';

// 前端使用的邀请码类型（camelCase + 字符串状态）
export interface InviteCode {
  id: string;
  code: string;
  status: 'active' | 'used' | 'expired' | 'deleted';
  createdAt: string;
  usedAt?: string;
  usedBy?: string;
  expiresAt?: string;
  creatorUid?: string;
}

// 状态枚举转换函数
function convertStatus(
  status: InviteCodeStatus,
): 'active' | 'used' | 'expired' | 'deleted' {
  switch (status) {
    case InviteCodeStatus.INVITE_CODE_STATUS_ACTIVE:
      return 'active';
    case InviteCodeStatus.INVITE_CODE_STATUS_USED:
      return 'used';
    case InviteCodeStatus.INVITE_CODE_STATUS_EXPIRED:
      return 'expired';
    case InviteCodeStatus.INVITE_CODE_STATUS_DELETED:
      return 'deleted';
    default:
      return 'active';
  }
}

// 字段转换函数：将 API 返回的 snake_case 格式转换为前端使用的 camelCase 格式
function transformInviteCode(apiCode: ApiInviteCode): InviteCode {
  return {
    id: apiCode.id,
    code: apiCode.code,
    status: convertStatus(apiCode.status),
    createdAt: apiCode.created_at,
    usedAt: apiCode.used_at,
    usedBy: apiCode.used_by,
    expiresAt: apiCode.expires_at,
    creatorUid: apiCode.creator_uid,
  };
}

class InviteCodeStore {
  inviteCodes: InviteCode[] = [];
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  searchKeyword = '';
  statusFilter: InviteCodeStatus | undefined = undefined;
  pageTokens: string[] = [''];
  hasMore = false;
  scopeAppId = '';
  loading = false;
  private requestSequence = 0;

  constructor() {
    makeAutoObservable(this);
  }

  private async fetchInviteCodesForApp(appId: string) {
    const requestSequence = ++this.requestSequence;
    const requestedPage = this.pagination.page;
    const pageToken =
      requestedPage === 1 ? '' : this.pageTokens[requestedPage - 1] || '';
    const response = await inviteCodeApi.listInviteCodes({
      app_id: appId,
      page_num: requestedPage,
      page_size: this.pagination.pageSize,
      page_token: pageToken || undefined,
      keyword: this.searchKeyword || undefined,
      status: this.statusFilter,
    });

    runInAction(() => {
      if (
        requestSequence !== this.requestSequence ||
        appId !== this.scopeAppId ||
        requestedPage !== this.pagination.page
      ) {
        return;
      }

      const nextPageToken = response.next_page_token || '';
      this.inviteCodes = response.invite_codes.map(transformInviteCode);
      this.pagination.total = response.total;
      this.hasMore =
        response.has_more ??
        requestedPage * this.pagination.pageSize < response.total;
      this.pageTokens = this.pageTokens.slice(0, requestedPage);
      if (nextPageToken) {
        this.pageTokens[requestedPage] = nextPageToken;
      }
      this.loading = false;
    });
  }

  // 获取邀请码列表
  async fetchInviteCodes() {
    const appId = brandStore.requireActiveBrandAppId();
    this.setAppScope(appId);
    this.loading = true;
    try {
      await this.fetchInviteCodesForApp(appId);
    } catch (error) {
      runInAction(() => {
        if (appId === this.scopeAppId) {
          this.loading = false;
        }
      });
      throw error;
    }
  }

  // 创建邀请码
  async createInviteCode(
    data: CreateInviteCodeRequest,
  ): Promise<CreateInviteCodeReply> {
    const appId = brandStore.requireActiveBrandAppId();
    this.setAppScope(appId);
    this.loading = true;
    try {
      const reply = await inviteCodeApi.createInviteCode({
        ...data,
        app_id: appId,
      });
      if (appId !== this.scopeAppId) {
        return reply;
      }
      // 新记录位于排序最前面，回到首页重建游标链。
      runInAction(() => this.resetPagination());
      try {
        await this.fetchInviteCodesForApp(appId);
      } catch (refreshError) {
        // 邀请码已经创建成功，列表刷新失败不应吞掉本次生成结果。
        runInAction(() => {
          if (appId === this.scopeAppId) {
            this.loading = false;
          }
        });
        console.error('生成邀请码后刷新列表失败:', refreshError);
      }
      return reply;
    } catch (error) {
      runInAction(() => {
        if (appId === this.scopeAppId) {
          this.loading = false;
        }
      });
      throw error;
    }
  }

  // 生成邀请码（保持向后兼容的别名）
  async generateInviteCode(data: CreateInviteCodeRequest) {
    return this.createInviteCode(data);
  }

  // 删除邀请码
  async deleteInviteCode(id: string) {
    const appId = brandStore.requireActiveBrandAppId();
    this.setAppScope(appId);
    this.loading = true;
    try {
      await inviteCodeApi.deleteInviteCode({ id, app_id: appId });
      if (appId !== this.scopeAppId) {
        return;
      }
      await this.fetchInviteCodesForApp(appId);
      if (appId !== this.scopeAppId) {
        return;
      }
      if (this.inviteCodes.length === 0 && this.pagination.page > 1) {
        runInAction(() => {
          this.pagination.page -= 1;
        });
        await this.fetchInviteCodesForApp(appId);
      }
    } catch (error) {
      runInAction(() => {
        if (appId === this.scopeAppId) {
          this.loading = false;
        }
      });
      throw error;
    }
  }

  // 给用户加内测资格：直接授予 beta access
  async grantBetaAccess(userId: string) {
    const appId = brandStore.requireActiveBrandAppId();
    this.loading = true;
    try {
      await inviteCodeApi.grantBetaAccess({ user_id: userId, app_id: appId });
      runInAction(() => {
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        if (appId === this.scopeAppId) {
          this.loading = false;
        }
      });
      throw error;
    }
  }

  // 单条发送/重发邀请码到邮箱
  async sendInviteCode(id: string, email: string) {
    const appId = brandStore.requireActiveBrandAppId();
    await inviteCodeApi.sendInviteCode({ id, to_email: email, app_id: appId });
  }

  // 批量发送邀请码（每个邮箱生成一个新码），返回结果供 UI 展示
  async batchSendInviteCodes(
    emails: string[],
  ): Promise<BatchSendInviteCodesReply> {
    const appId = brandStore.requireActiveBrandAppId();
    this.setAppScope(appId);
    this.loading = true;
    try {
      const reply = await inviteCodeApi.batchSendInviteCodes({
        emails,
        app_id: appId,
      });
      if (appId !== this.scopeAppId) {
        return reply;
      }
      // 批量新增后回到首页，避免沿用旧游标看不到新数据。
      runInAction(() => this.resetPagination());
      await this.fetchInviteCodesForApp(appId);
      return reply;
    } catch (error) {
      runInAction(() => {
        if (appId === this.scopeAppId) {
          this.loading = false;
        }
      });
      throw error;
    }
  }

  // 设置搜索关键词
  setSearchKeyword(keyword: string) {
    const changed = this.searchKeyword !== keyword;
    this.searchKeyword = keyword;
    this.resetPagination();
    return changed;
  }

  // 设置状态筛选
  setStatusFilter(status: InviteCodeStatus | undefined) {
    this.statusFilter = status;
    this.resetPagination();
  }

  // 游标只能按顺序生成，管理端因此只允许前后翻页。
  setPagination(page: number) {
    if (
      page < 1 ||
      page > this.pagination.page + 1 ||
      (page > this.pagination.page && !this.hasMore)
    ) {
      return;
    }
    this.pagination.page = page;
  }

  setAppScope(appId: string) {
    if (this.scopeAppId === appId) {
      return false;
    }
    this.scopeAppId = appId;
    this.inviteCodes = [];
    this.resetPagination(true);
    return true;
  }

  private resetPagination(clearTotal = false) {
    this.pagination.page = 1;
    this.pageTokens = [''];
    this.hasMore = false;
    if (clearTotal) {
      this.pagination.total = 0;
    }
  }
}

export const inviteCodeStore = new InviteCodeStore();
